import React from 'react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaShieldAlt, FaRocket, FaChartLine } from 'react-icons/fa';

const VisualProcess = () => {
    const processes = [
        {
            step: "01",
            title: "Envision & Ideation",
            description: "Spark innovation with strategic ideation and creative brainstorming. We help global brands imagine powerful digital experiences and growth opportunities.",
            icon: <FaLightbulb className="text-4xl" />,
            image: "Envision & Ideation.webp",
            color: "purple",
            position: "left",
            features: [
                "Market research & analysis",
                "Competitor benchmarking",
                "Innovation workshops",
                "Product strategy development"
            ]
        },
        {
            step: "02",
            title: "Establish Brand Trust",
            description: "Build credibility through exceptional user experience, service quality, and brand consistency. We nurture long-lasting relationships for international markets.",
            icon: <FaShieldAlt className="text-4xl" />,
            image: "Establish Brand Trust.webp",
            color: "amber",
            position: "right",
            features: [
                "Brand identity design",
                "UI/UX optimization",
                "Customer journey mapping",
                "Trust-building strategies"
            ]
        },
        {
            step: "03",
            title: "Execute Digital Strategy",
            description: "Implement precise web, app & marketing solutions that drive ROI. Our global execution approach ensures measurable results across India, USA, Australia & Europe.",
            icon: <FaRocket className="text-4xl" />,
            image: "Execute Digital Strategy.webp",
            color: "green",
            position: "left",
            features: [
                "Agile development",
                "Performance marketing",
                "Technology implementation",
                "Cross-platform deployment"
            ]
        },
        {
            step: "04",
            title: "Echo & Optimize",
            description: "Amplify success with continuous improvement, customer feedback analysis, and market adaptation. We turn performance data into scalable growth strategies.",
            icon: <FaChartLine className="text-4xl" />,
            image: "Echo & Optimize.webp",
            color: "blue",
            position: "right",
            features: [
                "Data analytics & insights",
                "Conversion rate optimization",
                "Iterative improvements",
                "Scaling strategies"
            ]
        }
    ];

    const colorMap = {
        purple: {
            gradient: "from-purple-600 to-violet-600",
            light: "bg-purple-500/10",
            text: "text-purple-400"
        },
        amber: {
            gradient: "from-amber-500 to-orange-500",
            light: "bg-amber-500/10",
            text: "text-amber-400"
        },
        green: {
            gradient: "from-emerald-500 to-teal-600",
            light: "bg-emerald-500/10",
            text: "text-emerald-400"
        },
        blue: {
            gradient: "from-blue-500 to-cyan-500",
            light: "bg-blue-500/10",
            text: "text-blue-400"
        }
    };

    return (
        <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-gray-900 relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden opacity-10">
                <div className="absolute top-0 left-0 w-64 h-64 bg-purple-900 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-900 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
                <div className="absolute bottom-0 left-1/2 w-64 h-64 bg-emerald-900 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Headline with Animation */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-20"
                >
                    <span className="inline-block px-4 py-2 text-sm font-semibold text-purple-400 bg-purple-900/30 rounded-full mb-4">
                        OUR PROVEN METHODOLOGY
                    </span>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                            Strategic Process,
                        </span><br />
                        <span className="text-white">Exceptional Results</span>
                    </h2>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                        Our systematic approach delivers measurable outcomes for global businesses
                    </p>
                </motion.div>

                {/* Visual Process Timeline */}
                <div className="space-y-32 relative">
                    {/* Vertical connector line */}
                    <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-500/30 via-blue-500/30 to-emerald-500/30 -translate-x-1/2" />

                    {processes.map((process, index) => {
                        const colors = colorMap[process.color];
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: process.position === 'left' ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true, margin: "-100px" }}
                                className={`relative flex flex-col ${process.position === 'right' ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 md:gap-16 items-center`}
                            >
                                {/* Step Indicator */}
                                <div className={`absolute ${process.position === 'left' ? 'md:left-1/2 -left-4' : 'md:right-1/2 -right-4'} top-0 md:top-1/2 z-10 md:-translate-y-1/2`}>
                                    <div className={`w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-2xl bg-gradient-to-br ${colors.gradient} shadow-lg border-4 border-gray-900`}>
                                        {process.step}
                                    </div>
                                </div>

                                {/* Image Container */}
                                <div className={`flex-1 ${process.position === 'left' ? 'md:pr-8' : 'md:pl-8'} w-full`}>
                                    <motion.div
                                        whileHover={{ scale: 1.02 }}
                                        className={`p-1 rounded-2xl bg-gradient-to-br ${colors.gradient} bg-opacity-30 shadow-xl`}
                                    >
                                        <div className="bg-gray-800 rounded-xl overflow-hidden p-8 flex items-center justify-center min-h-[300px]">
                                            <img
                                                src={process.image}
                                                alt={process.title}
                                                className="w-full h-full max-h-64 object-contain"
                                                loading="lazy"
                                            />
                                        </div>
                                    </motion.div>
                                </div>

                                {/* Text Content */}
                                <div className="flex-1 w-full">
                                    <div className="inline-block md:text-left mb-6">
                                        <span className={`text-xs font-semibold tracking-wider uppercase ${colors.text}`}>
                                            Phase {process.step}
                                        </span>
                                        <div className="flex items-center gap-4 mt-3 mb-4">
                                            <div className={`p-3 rounded-lg ${colors.light} ${colors.text}`}>
                                                {process.icon}
                                            </div>
                                            <h3 className="text-3xl font-bold text-white">
                                                {process.title}
                                            </h3>
                                        </div>
                                    </div>
                                    <p className="text-lg text-gray-300 md:text-left mb-6">
                                        {process.description}
                                    </p>

                                    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                                        <h4 className="font-semibold text-white mb-3">Key Activities:</h4>
                                        <ul className="space-y-2">
                                            {process.features.map((feature, i) => (
                                                <li key={i} className="flex items-start gap-2">
                                                    <span className={`mt-1 ${colors.text}`}>•</span>
                                                    <span className="text-gray-300">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>


            </div>
        </section>
    );
};

export default VisualProcess;